import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { SignUpForm } from "../components/auth/SignUpForm";
import { RegistrationComplete } from "../components/auth/RegistrationComplete";
import { EmailConfirmation } from "../components/auth/EmailConfirmation";
import { AuthService } from "../services/auth.service.real";
import { SignUpSchemaType } from "../domain/validation/auth.schema";
import { User } from "../domain/types/auth.types";
import { ErrorMessage } from "../components/ui/ErrorMessage";

type SignUpStep = "form" | "email-confirmation" | "complete";

export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<SignUpStep>("form");
  const [userData, setUserData] = useState<User | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [error, setError] = useState<string>("");

  const signUpMutation = useMutation({
    mutationFn: (data: SignUpSchemaType) => AuthService.signUp(data),
    onSuccess: (result) => {
      setUserData(result.user);
      // Email verification link will be sent to email
      // Move to email confirmation step
      setStep("email-confirmation");
      setError(""); // Clear any previous errors
    },
    onError: (error: any) => {
      // Handle API errors
      const errorMessage = error?.message || "An error occurred during sign up. Please try again.";
      setError(errorMessage);
    },
  });

  const handleSignUp = async (data: SignUpSchemaType) => {
    // Clear any previous errors
    setError("");
    // Store the email for confirmation message
    setUserEmail(data.email);

    // Store credentials temporarily for email verification auto-login
    localStorage.setItem(
      "signupCredentials",
      JSON.stringify({
        email: data.email,
        password: data.password,
      })
    );

    // Only send the fields the API expects
    const registrationData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      organisationName: data.organisationName,
      confirmPassword: "", // Not needed by API
      smsConsent: data.smsConsent,
    };

    try {
      await signUpMutation.mutateAsync(registrationData);
    } catch (err: any) {
      // Error will be handled by onError callback in useMutation
      // This catch block prevents unhandled promise rejections
    }
  };

  const handleDismissError = () => {
    setError("");
  };

  const handleComplete = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-arial bg-primary-blue">
      <div>
        {step === "form" && (
          <>
            <ErrorMessage message={error} onDismiss={handleDismissError} className="max-w-2xl" />
            <SignUpForm onSubmit={handleSignUp} isLoading={signUpMutation.isPending} />
          </>
        )}

        {step === "email-confirmation" && <EmailConfirmation email={userEmail} type="signup" />}

        {step === "complete" && <RegistrationComplete onContinue={handleComplete} />}
      </div>
    </div>
  );
};
